import { Box, Button, Grid, IconButton, Tooltip } from "@mui/material";
import { BubbleMenu } from "@tiptap/react";
import { Editor as TipTapEditor } from "@tiptap/react";
import { SubscriptionContext } from "contexts/Subscription";
import { Dispatch, SetStateAction, useContext, useState } from "react";
import {
  BentoBrand,
  BentoBrandTier,
  PreviewMessage,
  UpgradeDialogSource,
} from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";
import { isOnPage, routes } from "schemas/routes";

import { LinkModal } from "components/Editor/LinkModal";
import MenuOptions from "components/Editor/Menu";
import { VariableModal } from "components/Editor/Variable/VariableModal";
import { AttachmentProps } from "components/Editor/quicksend";
import StyledButton from "components/Styled/Button";
import { HandleSendEmail } from "features/Influencer/ContactList/schema";
import { useCollection } from "utils/useCollection";
import { useDrafts } from "utils/useDrafts";
import { Url, useLinkEditor } from "utils/useLinkEditor";
import { usePageSize } from "utils/usePageSize";
import { useVariableEditor } from "utils/useVariablesEditor";

import CustomizerHelper from "../CustomizerHelper";
import QuickSendButtonGroup from "./ButtonGroup";
import ScheduledTime from "./ScheduledTime";
import styles from "./styles";

interface Props {
  handleSendEmail: HandleSendEmail;
  sendLoading: boolean;
  attachmentProps: AttachmentProps;
  preview: PreviewMessage;
  index: number;
  editor: TipTapEditor;
  setUpdateOrgProfile?: Dispatch<SetStateAction<boolean>>;
  disabledSend?: boolean;
  hideCustomizeHelper?: boolean;
  showTestEmail?: boolean;
  bentoBrand?: BentoBrand | null;
}

export default function BottomToolbar({
  handleSendEmail,
  sendLoading,
  editor,
  attachmentProps,
  preview,
  index,
  setUpdateOrgProfile,
  bentoBrand,
  disabledSend = false,
  showTestEmail = false,
  hideCustomizeHelper = false,
}: Props) {
  const { hasBanner } = usePageSize();
  const [openBottomToolBar, setOpenBottomToolBar] = useState(true);

  const { removeDraftApiCall, removeDraftFromList } = useDrafts();
  const { removeDraftFromCollection } = useCollection();
  const { subscription, setUpgradeDialogSource } =
    useContext(SubscriptionContext);

  const {
    modalIsOpen,
    url,
    setUrl,
    openModal,
    closeModal,
    saveLink,
    removeLink,
  } = useLinkEditor({ editor });

  const {
    variable,
    variableModalIsOpen,
    closeVariableModal,
    handleSaveVariable,
    openVariableFilledModal,
    deleteVariableFilled,
  } = useVariableEditor({ editor });

  const { openAttachmentManager, setSelectImageOpen } = attachmentProps;

  const handleClick = () => {
    setOpenBottomToolBar((prev) => !prev);
  };

  const handleDelete = (e: CustomEvent) => {
    if (!bentoBrand) return;
    removeDraftApiCall(e, bentoBrand);
    removeDraftFromList(bentoBrand);
    removeDraftFromCollection(bentoBrand);
  };

  const renderDeleteButton = () => {
    return (
      isOnPage(routes.recommendations) &&
      bentoBrand && (
        <Tooltip title="Remove draft">
          <IconButton sx={{ fontSize: 16 }} onClick={handleDelete}>
            <Box component="i" className="fa-regular fa-trash" />
          </IconButton>
        </Tooltip>
      )
    );
  };

  return (
    <Grid
      container
      item
      sx={styles.bottomBarAbsolute(hasBanner)}
      alignItems="flex-end"
      gap={1}
      justifyContent={"space-between"}
    >
      {openBottomToolBar && (
        // Important: this prevent onBlur event from triggering on TipTap editor.
        <Grid container item xs={12}>
          <Box
            sx={{ boxShadow: 2, px: 1, background: "white" }}
            onMouseDown={(e) => e.preventDefault()}
          >
            {editor && (
              <MenuOptions
                hideLink={true}
                editor={editor}
                openModal={openModal}
                isBubble={true}
                openVariableFilledModal={openVariableFilledModal}
                handleDeleteVariable={deleteVariableFilled}
                fromQuickSend={true}
              />
            )}
          </Box>
        </Grid>
      )}

      <Grid
        container
        item
        xs={12}
        sx={styles.sendSection}
        alignItems="center"
        justifyContent={"space-between"}
      >
        <Grid container item xs="auto" alignItems="center" gap={0.5}>
          {editor && (
            <>
              <IconButton
                sx={[styles.iconButton, openBottomToolBar ? styles.open : {}]}
                onClick={handleClick}
                onMouseDown={(e) => e.preventDefault()}
              >
                <Box component="i" className="fa-thin fa-font" />
              </IconButton>
              <IconButton
                onClick={openModal}
                sx={[
                  styles.iconButton,
                  editor.isActive("link") ? styles.open : {},
                ]}
              >
                <Box component="i" className="fa-solid fa-link" />
              </IconButton>
              <IconButton
                onClick={openAttachmentManager}
                sx={styles.iconButton}
              >
                <Box component="i" className="fa-solid fa-paperclip" />
              </IconButton>
              <IconButton
                sx={[
                  styles.iconButton,
                  editor.isActive("custom-image") ? styles.open : {},
                ]}
                onClick={() => setSelectImageOpen(true)}
              >
                <Box component="i" className="fa-solid fa-image" />
              </IconButton>
            </>
          )}

          {!hideCustomizeHelper && bentoBrand && (
            <CustomizerHelper bentoBrand={bentoBrand} />
          )}
        </Grid>

        <Grid container item xs alignItems="center" justifyContent="flex-end">
          {renderDeleteButton()}
          {bentoBrand?.tier === BentoBrandTier.tier_one &&
          !subscription?.isSubscribed ? (
            <Button
              variant="contained"
              size="small"
              disableElevation
              sx={{
                ".MuiButtonGroup-grouped:not(:last-of-type)": {
                  borderColor: "secondary.light",
                },
              }}
              onClick={() =>
                setUpgradeDialogSource(UpgradeDialogSource.exclusive_brand)
              }
            >
              Upgrade to Send
            </Button>
          ) : (
            <QuickSendButtonGroup
              index={index}
              handleSendEmail={handleSendEmail}
              sendLoading={sendLoading}
              disabled={disabledSend}
              hideTestButton={!hideCustomizeHelper}
              showTestEmail={showTestEmail}
            />
          )}
        </Grid>
        <LinkModal
          url={url}
          closeModal={closeModal}
          open={modalIsOpen}
          onChangeUrl={(newUrl: Url) => setUrl(newUrl)}
          onSaveLink={saveLink}
          onRemoveLink={removeLink}
        />

        <VariableModal
          variable={variable}
          open={variableModalIsOpen}
          closeModal={closeVariableModal}
          onSave={handleSaveVariable}
          setUpdateOrgProfile={setUpdateOrgProfile}
        />

        <BubbleMenu
          tippyOptions={{ duration: 50, zIndex: 1201, placement: "bottom" }}
          editor={editor}
          shouldShow={({ editor, from, to }) => {
            // only show the bubble menu for links.
            return from === to && editor.isActive("link");
          }}
        >
          <StyledButton
            onClick={openModal}
            onMouseDown={(e) => e.preventDefault()}
          >
            Edit Link{" "}
            <Box component="i" className="fa-regular fa-pen" sx={{ ml: 1 }} />
          </StyledButton>
        </BubbleMenu>

        <Grid item xs={12}>
          <ScheduledTime preview={preview} index={index} />
        </Grid>
      </Grid>
    </Grid>
  );
}
